import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';
import { createCoalescedRunner } from '@/renderer/utils/coalesce';
import { useReconcileOnForeground } from '@/renderer/utils/hooks/useReconcileOnForeground';
import { useReconcileOnReconnect } from '@/renderer/utils/hooks/useReconcileOnReconnect';
import { useAuthToken } from '@/renderer/utils/useMe';
import useWebSocket, { WS_BASE_URL } from '@/renderer/utils/useWebSocket2';

// Non-future pending — matches the My Results pending section (PendingSection)
// filter exactly, so this count and that section always agree.
const NON_FUTURE_PLAY_TYPES = ['side', 'total', 'prop', 'prop-group'] as const;

// A release NOTIFY delivers a burst of messageReceipt/created + /updated events,
// and this hook is mounted twice (nav badge + release-center notice), each with
// its own WS subscription. Before #1801 every event triggered an immediate,
// un-throttled refetch of the expensive limit-100 getPendingWithGhosts query, so
// one release became ~2N simultaneous pending queries across N online clients and
// 503'd the DB pool. Coalesce the whole burst — across both mounts — into ONE
// trailing refetch. Both mounts read the same shared infinite-store bank (same
// `navPendingCount` key), so a single refetch updates the count everywhere.
const PENDING_REFETCH_DEBOUNCE_MS = 1000;
// ...and spread the result across clients (#2059). Coalescing removed the
// per-client fan-out but left every client firing at the same +1000ms off the
// same broadcast event, so the fleet still arrived as one spike. Landing
// uniformly in 1.0-3.0s costs nothing here — this only refreshes a badge count,
// and the pick itself renders live from the WS event.
const PENDING_REFETCH_JITTER_MS = 2000;
const pendingRefetchRunner = createCoalescedRunner(
  PENDING_REFETCH_DEBOUNCE_MS,
  PENDING_REFETCH_JITTER_MS,
);

/**
 * The signed-in member's count of ungraded, non-future pending picks across all
 * sports — the SAME entitlement-based source the My Results pending section uses.
 *
 * `type: 'release' + result: 'pending'` routes this to the server's
 * `getPendingWithGhosts`, so the count includes "ghost" picks the member is
 * entitled to via an active membership even without a messageReceipt (a fresh or
 * returning subscriber). That's why a sub with no receipts still gets a correct
 * "N pending" instead of 0.
 *
 * Shared by the nav badge (PendingCountBadge) and the Release Center pending
 * affordance. Both mount this with the same infinite-store key, so they share one
 * request and can never disagree.
 *
 * Live-updates (from #1455/#1459): this request skips polling and has no infinite-
 * store WS subscription of its own, so nothing would refetch it while it stays
 * mounted. Subscribe to the same live signals the rest of the app uses and re-derive
 * the count from the server on each: a new release (`created`) ticks it up, a grade
 * (`updated`) drops the play from the `pending` filter and ticks it down.
 *
 * Reconcile (self-heal, #1244): the live path above can silently drift stale — a
 * WebSocket reconnect gap, a backgrounded/suspended tab, an offline stretch, or the
 * coalescer dropping part of a burst can all leave a `created`/`updated` unseen, and
 * the badge would then stay wrong until a full page reload re-fetched it (the reported
 * "badge stuck at 1 while My Results shows 11"). So reconcile the count on the same
 * signals the release center uses (#1210/#1659): on WebSocket (re)connect and on tab
 * refocus/foreground, re-run the count. Both go through the SAME `pendingRefetchRunner`
 * coalescer as the live events, so a reconnect that also delivers a NOTIFY burst still
 * collapses into ONE trailing refetch and never reintroduces the release-time fan-out
 * (#1801). `skipPoll: true` is kept — no periodic poll is added — reconnect + foreground
 * reconcile is enough to self-heal without a reload.
 */
export function usePendingPicksCount(): { count: number; loggedIn: boolean } {
  const authToken = useAuthToken();
  const loggedIn = !!authToken;

  const request = useNextInfiniteRequest<
    API.MessageReceipt,
    void,
    API.WithPageInput<API.ExtractNestedSortable<API.MessageReceipt>>
  >(
    useMemo(
      () => ({
        key: 'navPendingCount',
        req: '/api/v1/messageReceipt/me',
        query: {
          page: { limit: 100, offset: 0 },
          data: {
            type: 'release',
            result: 'pending',
            playType: [...NON_FUTURE_PLAY_TYPES],
          },
        },
        skipPoll: true,
        pause: !loggedIn,
        // A count of 0 until it lands just hides the badge / notice; never hold a
        // page on it.
        suspendOnClient: false,
      }),
      [loggedIn],
    ),
  );

  const refetchRef = useRef(request.refetch);
  refetchRef.current = request.refetch;

  // Register this mount's refetch with the shared coalescer while mounted. The
  // coalescer only ever runs one registrant per burst; since both mounts share
  // the same store bank, that single refetch updates the count for all of them.
  useEffect(() => {
    if (!loggedIn) {
      return;
    }
    return pendingRefetchRunner.register(() => refetchRef.current?.());
  }, [loggedIn]);

  const onReceiptChanged = useCallback(() => {
    pendingRefetchRunner.schedule();
  }, []);

  const ws = useWebSocket(
    WS_BASE_URL,
    useMemo(
      () => ({
        connect: loggedIn,
        subscriptions: {
          'messageReceipt/created': onReceiptChanged,
          'messageReceipt/updated': onReceiptChanged,
        },
      }),
      [loggedIn, onReceiptChanged],
    ),
  );

  // Self-heal the count when the live path may have missed an event. Both signals
  // route through the same coalescer as the WS handler (via `onReceiptChanged`), so
  // a reconnect that arrives alongside a release NOTIFY burst still collapses into a
  // single trailing refetch — no release-time fan-out (#1801).
  //
  // On WebSocket (re)connect: a reconnect gap can drop a `created`/`updated`, so
  // catch up from the server the moment the socket is back (#1210). Guard on
  // `loggedIn` so a paused socket's `ready` state can't schedule a refetch.
  useReconcileOnReconnect(loggedIn && ws.ready, onReceiptChanged);

  // On tab refocus/foreground: a backgrounded tab can have its socket throttled and
  // resume with no reconnect, so mirror the app badge / reconcile primitive and
  // re-derive the count on wake (#1659). Cheap when signed out: `onReceiptChanged`
  // schedules through the coalescer, which no-ops when no mount is registered
  // (the `register` effect only runs while `loggedIn`).
  useReconcileOnForeground(onReceiptChanged);

  // getPendingWithGhosts sets pageInfo.total to the ghost-inclusive count when it
  // augments with ghosts; otherwise it skips the COUNT (#1801) and total is
  // absent, so fall back to the returned rows (the exact count while under the
  // limit-100 page — which a member's pending slate always is).
  const count = request.data?.pageInfo?.total ?? request.data?.data?.length ?? 0;

  return { count: loggedIn ? count : 0, loggedIn };
}
